import React, { useCallback } from 'react';
import styled, { css } from 'styled-components';
import useSelectedTransport from '@/app/components/hotel-detail/hooks/useSelectedTransport';
import ChangeTransportFilters from '@/app/components/hotel-detail/changeTransport/ChangeTransportFilters';
import ChangeTransportList from '@/app/components/hotel-detail/changeTransport/ChangeTransportList';
import { Placeholder, Text } from '@lmn/design-system';
import ChangeTransportSorting from '@/app/components/hotel-detail/changeTransport/ChangeTransportSorting';
import { trackSelectNewTransport } from '@/app/components/hotel-detail/trackInteractionEvents';
import { useTranslation } from '@/app/components/translations/Translation';
import { useOffersContext } from '@/app/components/hotel-detail/OffersProvider';
import { useIsLgOrGreater } from '@/app/components/hotel-detail/hooks/useMediaSSR';
import { useInitialParams } from '@/app/components/hotel-detail/InitialParamsProvider';
import customCss from '@/app/components/hotel-detail/css/customCss';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useFetchRooms } from '@/app/components/hotel-detail/hooks/useFetchRooms';
import { ChangeTransportFiltersReset } from '@/app/components/hotel-detail/changeTransport/ChangeTransportFiltersReset';
import { useCreateAsyncPricing } from '../PricingProvider';
import { useUrlSearchParams } from '../UrlSearchParamsProvider';
import { ChangeTransportDetailsModal } from './ChangeTransportDetailsModal';
import { fetchWithTracingAndLogging } from '../common/fetchWithTracing';
import { BASE_PATH } from '../../search/utils/path';
import { useTransportIsChangingFilters } from './ChangeTransportFiltersProvider';
import { useCuriosity } from '../CuriosityProvider';
import { getLoggingInfo } from '../lib/loggingInfo';
import { AccommodationCard } from '../flight-train/TransportAccommodationCard';
import useTransportsFetch from '../travel-api/transportsFetch';
import { logChangeTransportFailure } from './logChangeTransportFailure';

const FiltersContainer = styled.div`
  width: 260px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  row-gap: 16px;
`;

const TransportListContainer = styled.div`
  flex-grow: 1;
  position: relative;
  display: flex;
  flex-direction: column;
  row-gap: 16px;
`;

export default function ChangeTransportPageLayout() {
  const { data: asyncPricingData } = useCreateAsyncPricing();
  const { clearPreselectedTransport } = useOffersContext();
  const { data, mutate: mutateRooms, isValidating: roomsValidating } = useFetchRooms();
  const { data: selectedTransportData } = useSelectedTransport();
  const { setChanging } = useTransportIsChangingFilters();
  const roomsData = roomsValidating ? null : (data as RoomsServiceResponseType);
  const urlSearchParams = useUrlSearchParams();
  const initialParams = useInitialParams();
  const { searchInfoData, commonData, localization } = usePageWrapperData();
  const { sendCuriosityClickEvent } = useCuriosity();
  const { t } = useTranslation();
  const isDesktop = useIsLgOrGreater(!commonData.isMobileDevice);
  const { data: travelTransports, error: transportsFetchError, isFallbackTransport } = useTransportsFetch();
  const pageConfiguration = (travelTransports as FullAvailableTransportResponse)?.pageConfiguration;
  const noAvailableTransports = (travelTransports as FullAvailableTransportResponse)?.availableTransports?.length === 0;

  const onChangeTransport = useCallback(
    (id: string, transportClass: string) => {
      setChanging(true);
      clearPreselectedTransport();
      sendCuriosityClickEvent({
        event_name: 'click_change_transport',
      });
      const changeTransportRequest: TravelChangeTransportRequest = {
        businessProfileId: localization.businessProfile,
        pricingId: asyncPricingData?.pricingId || '',
        pageType: initialParams.pageType,
        newTransportId: id || '',
        departureAirport: origin,
        transportClass,
        lang: localization.lang,
        locale: localization.locale,
        country: localization.country,
      };
      fetchWithTracingAndLogging(
        `${BASE_PATH}/api/hotel-detail/change-transport`,
        {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify(changeTransportRequest),
        },
        getLoggingInfo(initialParams.pageType, 'travel-api-change-transport', 'change-transport'),
        searchInfoData.searchTraceParent,
        commonData
      )
        .then(() => {
          trackSelectNewTransport(urlSearchParams.isTrain);
          mutateRooms(roomsData ? { ...roomsData, changedTransport: true } : undefined);
        }, logChangeTransportFailure)
        .finally(() => setChanging(false));
    },

    [selectedTransportData?.transports[0]?.id]
  );

  return (
    // use display flex only on desktop breakpoint when filters are on the page. In the other case is not needed
    // and causes a problem with the ellipsis on the origin-destination field.
    <div
      css={css`
        gap: 24px;

        @media ${customCss.breakpoints.sm} {
          display: flex;
          min-height: unset;
          margin: 0;
        }
      `}
    >
      {isDesktop && !noAvailableTransports && !transportsFetchError && !isFallbackTransport ? (
        <FiltersContainer>
          <ChangeTransportFilters />
          {(pageConfiguration?.filteredProducts ?? 0) < (pageConfiguration?.totalProducts ?? 0) ? (
            <ChangeTransportFiltersReset />
          ) : undefined}
        </FiltersContainer>
      ) : undefined}
      <TransportListContainer>
        {isDesktop && (
          <>
            <AccommodationCard sticky={true} />
            {!noAvailableTransports && !transportsFetchError && !isFallbackTransport && (
              <div
                css={`
                  display: flex;
                  flex-direction: column;
                  gap: 16px;
                `}
              >
                <Text
                  variant="Title2"
                  css={`
                    order: 1;
                  `}
                >
                  {t(urlSearchParams.isTrain ? 'vacation.oss.navigation.train' : 'vacation.oss.navigation.flight')}
                </Text>
                <ChangeTransportSorting
                  css={`
                    order: 3;
                  `}
                />
                {pageConfiguration ? (
                  <Text
                    variant="BodySmall"
                    css={css`
                      order: 2;
                      color: ${(p) => p.theme.kallisto.color.text.static.textSecondary};
                    `}
                  >
                    {t('vacation.common.filters.results.summary', {
                      filtered: pageConfiguration.filteredProducts.toString(),
                      total: pageConfiguration.totalProducts.toString(),
                    })}
                  </Text>
                ) : (
                  <div
                    css={`
                      order: 2;
                    `}
                  >
                    <Placeholder height="20px" width="100px" />
                  </div>
                )}
              </div>
            )}
          </>
        )}
        <ChangeTransportList />
        <ChangeTransportDetailsModal onChangeTransport={onChangeTransport} />
      </TransportListContainer>
    </div>
  );
}
